import React, {useState, forwardRef, useImperativeHandle, useEffect} from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import {alertIcErrorOutline24px} from '@ey/motif-react-wrapper/assets/icons/index';

import Icon from '../../../../../BaseComponentWrappers/MotifIcon';
import FormField from '../../../../../BaseComponentWrappers/MotifFormField';
import Input from '../../../../../BaseComponentWrappers/MotifInput';
import Label from '../../../../../BaseComponentWrappers/MotifLabel';
import Checkbox from '../../../../../BaseComponentWrappers/MotifCheckbox';
import ErrorMessage from '../../../../../BaseComponentWrappers/MotifErrorMessage';
import RichTextEditorWrapper from '../../../../../KendoEditor/RichTextEditorWrapper';
import {
	attributeTestingTypes as attributeTestingTypesList,
	maxLengthAttributeIndex,
	maxLengthAttributeDescription
} from '../../../../../../../util/uiconstants';
import {isNullOrEmpty, labels, numberFormattedText} from '../../../../../../../util/utils';
import {useUserPreferences} from '../../../../../../../util/customHooks';

const CreateControlAttributesForm = forwardRef(({data}, ref) => {
	const {numberFormat} = useUserPreferences();
	const [isValid, setIsValid] = useState(true);
	const [charCount, setCharCount] = useState(0);
	const [formValues, setFormValues] = useState({
		attributeIndex: '',
		attributeDescription: '',
		attributeTestingTypes: []
	});
	const [changeCount, setChangeCount] = useState(0);

	useEffect(() => {
		if (data) {
			setFormValues({
				attributeIndex: data.attributeIndex,
				attributeDescription: data.attributeDescription,
				attributeTestingTypes: data.testingTypes
			});
			setChangeCount(1);
		}
	}, [data]);

	useImperativeHandle(ref, () => ({
		validateForm: () => {
			if (
				isNullOrEmpty(formValues.attributeIndex) ||
				isNullOrEmpty(formValues.attributeDescription) ||
				charCount > maxLengthAttributeDescription ||
				formValues.attributeTestingTypes.length === 0
			) {
				setIsValid(false);
				return false;
			} else {
				setIsValid(true);
				return true;
			}
		},
		getFormValues: () => formValues,
		reset: () => {
			setFormValues({
				attributeIndex: '',
				attributeDescription: '',
				attributeTestingTypes: []
			});
		}
	}));

	const handleAttributeTestingTypesChange = (event) => {
		const {value, checked} = event.target;
		const typeValue = Number(value);

		if (checked) {
			return setFormValues({
				...formValues,
				attributeTestingTypes: [...formValues.attributeTestingTypes, typeValue]
			});
		}

		setFormValues({
			...formValues,
			attributeTestingTypes: formValues.attributeTestingTypes.filter((type) => type !== typeValue)
		});
	};

	const handleDescriptionChange = (text) => {
		setFormValues({
			...formValues,
			attributeDescription: text || ''
		});
	};

	const handleAttributeIndexChange = (event) => {
		const {value} = event.target;

		setFormValues({
			...formValues,
			attributeIndex: value
		});
	};

	const handleCharCount = (count) => {
		setCharCount(count);
	};

	return (
		<Form>
			<FormFieldWrapper $hasError={!isValid && isNullOrEmpty(formValues.attributeIndex)}>
				<FormField className="attribute-index" aria-label="Attribute Index" role="group">
					<Label for="attribute-index" position="in">
						{labels.requiredAttributeIndexLabel}
					</Label>
					<Input
						id="attribute-index"
						aria-label="Attribute Index"
						value={formValues.attributeIndex}
						onChange={handleAttributeIndexChange}
						required
						labelPosition="in"
						maxLength={maxLengthAttributeIndex}
						hideClearButton={false}
						suffix={
							!isValid && isNullOrEmpty(formValues.attributeIndex) ? (
								<Icon className="input-error-icon" src={alertIcErrorOutline24px} />
							) : null
						}
					/>
				</FormField>
				<ErrorMessage className="invalid-error-message" message={labels.errorMessageAttributeIndexRequired} />
			</FormFieldWrapper>
			<FormFieldWrapper
				className="richTextEditor-wrapper"
				$hasError={
					(formValues.attributeDescription.length === 0 && !isValid) || charCount > maxLengthAttributeDescription
				}
			>
				<StyledLabel id="attribute-description-label">{labels.requiredAttributeDescriptionLabel}</StyledLabel>
				<RichTextEditorWrapper
					id="attribute-description"
					uniqueId="attributeDescription"
					placeholder={labels.requiredAttributeDescriptionLabel}
					showCharacterCount
					charCount={charCount}
					showMaxCharacterCount
					useGuidedWorkFlowToolset
					value={formValues.attributeDescription}
					required
					resize={false}
					ofLabel={labels.of}
					charactersLabel={labels.characters}
					onChange={handleDescriptionChange}
					maxCharCount={maxLengthAttributeDescription}
					charCountChanged={handleCharCount}
					key={changeCount}
				/>

				<ErrorMessage
					className="invalid-error-message"
					message={
						charCount > maxLengthAttributeDescription
							? labels.errorMessageAttributeDescriptionMaxLength
									.replaceAll('{#}', numberFormattedText(numberFormat, charCount))
									.replaceAll('{##}', numberFormattedText(numberFormat, maxLengthAttributeDescription))
							: labels.errorMessageAttributeDescriptionRequired
					}
				/>
			</FormFieldWrapper>
			<FormFieldWrapper $hasError={!isValid && formValues.attributeTestingTypes.length === 0}>
				<StyledLabel>{labels.modalRequiredProceduresLabel}</StyledLabel>
				<FormField aria-label="Attributes Testing Types">
					<CheckboxWrapper $hasError={!isValid && formValues.attributeTestingTypes.length === 0} key={changeCount}>
						{Object.entries(attributeTestingTypesList).map(([id, value]) => (
							<Checkbox
								key={id}
								id={id}
								value={value}
								checked={formValues.attributeTestingTypes?.includes(value)}
								label={labels.attributeTestingTypesLabel[id]}
								onChange={handleAttributeTestingTypesChange}
							/>
						))}
					</CheckboxWrapper>
				</FormField>
				<ErrorMessage className="invalid-error-message" message={labels.errorMessageAttributeTestingTypesRequired} />
			</FormFieldWrapper>
		</Form>
	);
});

export default CreateControlAttributesForm;

CreateControlAttributesForm.displayName = 'CreateControlAttributesForm';

CreateControlAttributesForm.propTypes = {
	data: PropTypes.shape({
		attributeIndex: PropTypes.string,
		attributeDescription: PropTypes.string,
		attributeTestingTypes: PropTypes.arrayOf(PropTypes.number)
	})
};

const Form = styled.form`
	> section {
		margin-bottom: var(--px-8);
	}
	margin-bottom: var(--px-4);
`;

const StyledLabel = styled.p`
	font-size: var(--px-14);
	font-weight: 400;
	color: var(--neutrals-900);
	line-height: var(--px-20);

	&#attribute-description-label {
		position: absolute;
		top: 0;
		margin: 0;
	}
`;

const FormFieldWrapper = styled.section`
	.attribute-index {
		#attribute-index,
		.motif-input-component .motif-input-suffix {
			border-color: ${({$hasError}) => ($hasError ? 'var(--input-error--border-color)' : 'var(--input--border-color)')};
		}
	}

	.input-error-icon {
		color: var(--input-icon-error-icon-color);
		visibility: ${({$hasError}) => ($hasError ? 'visible' : 'hidden')};
	}

	.invalid-error-message {
		font-size: var(--px-12);
		visibility: ${({$hasError}) => ($hasError ? 'visible' : 'hidden')};
	}

	&.richTextEditor-wrapper {
		position: relative;
		.k-editor {
			border-color: ${({$hasError}) => ($hasError ? 'var(--input-error--border-color)' : 'var(--input--border-color)')};
			border-radius: var(--border-radius-medium);
			overflow: hidden;
			.k-toolbar {
				.k-button-group {
					.k-button,
					.k-dropdownlist,
					.k-picker {
						background-color: var(--input--bg-color);
						border: none;
					}
				}
			}
		}
	}
`;

const CheckboxWrapper = styled.section`
	display: flex;
	border: ${({$hasError}) => ($hasError ? 'var(--px-1) solid var(--input-error--border-color)' : '')};
	border-radius: var(--border-radius-medium);

	> span {
		padding: var(--px-10) var(--px-32) var(--px-10) 0;

		.motif-checkbox-label {
			font-size: var(--px-16);
		}
	}
`;
